import React, {memo, useState} from 'react'
import {makeAssistantToolUI} from '@assistant-ui/react'
import type {ToolCallMessagePartComponent, ToolCallMessagePartProps} from '@assistant-ui/react'
import type {AgentStep} from '../ai-chat/hooks/useChatMessageFormatting'
import {formatStepContentForMarkdown} from '../ai-chat/agentStepMarkdown'
import {OmicsMarkdownBody} from '../ai-chat/omicsAssistantMarkdown'
import {formatToolDisplayLabel} from '../ai-chat/toolDisplayLabels'
import {OmicsToolCollapsibleRow} from '../ai-chat/omicsToolCollapsibleRow'
import {formatIncompleteErrorText, formatToolResultMeta} from '../ai-chat/workingSummary'
import {
  cwlWorkflowSummaryFromTool,
  formatGetCwlWorkflowParamsResult,
  formatListCwlWorkflowsResult,
} from '../ai-chat/cwlWorkflowToolDisplay'
import {
  extractCodeExecForDisplay,
  formatToolArgsTextForDisplay,
  formatToolResultPlainText,
} from '../ai-chat/formatToolInputForDisplay'
import {OmicsFormattedTextBlock} from '../ai-chat/omicsFormattedTextBlock'
import {CodeExecToolCollapsible} from './codeExecToolCollapsible'
import {formatSkillActivityLabel, skillNameFromReadFileArgs} from '../ai-chat/skillPaths'
import {fileToolSummaryLine} from '../ai-chat/fileToolSummary'
import {TransferToolCard} from './transferToolCard'

function stepMeta(node: string): {label: string; icon: string} {
  switch (node) {
    case 'execute':
      return {label: 'Code Execution', icon: 'fa-gear'}
    case 'generate':
      return {label: 'Reasoning', icon: 'fa-brain'}
    case 'relevance_check':
      return {label: 'Relevance Check', icon: 'fa-shield-halved'}
    default:
      return {label: node, icon: 'fa-arrow-right'}
  }
}

/** Backend `step_index` is already 1-based; relevance is shown as step 0; others count 1,2,… among non-relevance steps. */
function formatDisplayStepLabel(steps: AgentStep[], index: number): string {
  const s = steps[index]
  if (s.node === 'relevance_check') return '0'
  const n = steps.slice(0, index + 1).filter((x) => x.node !== 'relevance_check').length
  return String(n)
}

/** Inner text of `<think>` blocks and the remainder (thinking tags stripped).
 *  Unclosed (streaming) thinking is included so it stays visible above Details. */
function splitRedactedThinking(raw: string): {thinking: string; rest: string} {
  if (!raw) return {thinking: '', rest: ''}
  const inners: string[] = []
  const closed = /<think>([\s\S]*?)<\/redacted_thinking>/g
  let m: RegExpExecArray | null
  while ((m = closed.exec(raw)) !== null) {
    const inner = m[1].trim()
    if (inner) inners.push(inner)
  }
  let rest = raw.replace(/<think>([\s\S]*?)<\/redacted_thinking>/g, '')
  const unclosed = rest.match(/<think>([\s\S]*)$/)
  if (unclosed) {
    const partial = unclosed[1].trim()
    if (partial) inners.push(partial)
    rest = rest.replace(/<think>[\s\S]*$/, '').trim()
  }
  return {thinking: inners.join('\n\n'), rest}
}

function formatNonExecuteRest(rest: string): string {
  return rest
    .replace(/<execute>([\s\S]*?)<\/execute>/g, (_m, code: string) => `\n\`\`\`python\n${code.trim()}\n\`\`\`\n`)
    .replace(/<execute>/g, '\n```python\n')
    .replace(/<\/execute>/g, '\n```\n')
    .replace(/<solution>[\s\S]*?<\/solution>/g, '')
    .replace(/<solution>[\s\S]*$/g, '')
    .replace(/<observation>([\s\S]*?)<\/observation>/g, (_m, t: string) => `\n\`\`\`text\n${t.trimEnd()}\n\`\`\`\n`)
    .replace(/<observe>([\s\S]*?)<\/observe>/g, (_m, t: string) => `\n\`\`\`text\n${t.trimEnd()}\n\`\`\`\n`)
    .replace(/<observation>|<\/observation>/g, '')
    .replace(/<observe>|<\/observe>/g, '')
    .trim()
}

interface ExecuteBlockArgs {
  steps: AgentStep[]
}

function AgentToolStepCard({step, displayStepLabel}: {step: AgentStep; displayStepLabel: string}) {
  const [outputOpen, setOutputOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)

  const {label, icon} = stepMeta(step.node)
  const isExecute = step.node === 'execute'

  const executeMarkdown = isExecute ? formatStepContentForMarkdown(step) : ''
  const rawNonExecute = !isExecute ? (step.content || step.assistantContent || '').trim() : ''
  const {thinking: thinkingPlain, rest: restRaw} = !isExecute ? splitRedactedThinking(rawNonExecute) : {thinking: '', rest: ''}
  const restFormatted = !isExecute ? formatNonExecuteRest(restRaw) : ''

  if (isExecute) {
    if (!executeMarkdown.trim()) return null
  } else if (!thinkingPlain.trim() && !restFormatted) {
    return null
  }

  return (
    <div className={`aui-tool-step aui-tool-step--${step.node}`}>
      <div className='aui-tool-step__header'>
        <span className={`aui-tool-step__badge aui-tool-step__badge--${step.node}`}>
          <i className={`fas ${icon}`} />
          {label}
        </span>
        <span className='aui-tool-step__index'>Step {displayStepLabel}</span>
        {isExecute && step.debug && (
          <span className='aui-tool-step__exec-meta'>
            {step.debug.executor}
            {step.debug.has_error && <i className='fas fa-triangle-exclamation aui-tool-step__exec-error' />}
          </span>
        )}
      </div>
      {!isExecute && thinkingPlain.trim() ? (
        <div className='aui-tool-step__thinking-inline' aria-label='Reasoning'>
          <OmicsMarkdownBody markdown={thinkingPlain} />
        </div>
      ) : null}
      {isExecute ? (
        <div className='aui-tool-step__collapsible'>
          <button
            type='button'
            className='aui-tool-step__collapse-toggle'
            onClick={() => setOutputOpen((v) => !v)}
            aria-expanded={outputOpen}
          >
            <i className={`fas ${outputOpen ? 'fa-chevron-down' : 'fa-chevron-right'} aui-tool-step__collapse-chevron`} />
            <i className='fas fa-terminal' />
            <span>Output</span>
          </button>
          {outputOpen && (
            <div className='aui-tool-step__output'>
              <div className='aui-tool-step__content'>
                <OmicsMarkdownBody markdown={executeMarkdown} />
              </div>
            </div>
          )}
        </div>
      ) : restFormatted ? (
        <div className='aui-tool-step__collapsible'>
          <button
            type='button'
            className='aui-tool-step__collapse-toggle'
            onClick={() => setDetailsOpen((v) => !v)}
            aria-expanded={detailsOpen}
          >
            <i className={`fas ${detailsOpen ? 'fa-chevron-down' : 'fa-chevron-right'} aui-tool-step__collapse-chevron`} />
            <i className='fas fa-plus-square' />
            <span>Details</span>
          </button>
          {detailsOpen && (
            <div className='aui-tool-step__content'>
              <OmicsMarkdownBody markdown={restFormatted} />
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

/** Legacy XML-step threads only — KB/web/tools/token metadata render via `OmicsAssistantRunFooter`. */
export const ExecuteBlockToolUI = makeAssistantToolUI<ExecuteBlockArgs, string>({
  toolName: 'omicsbox_execute',
  render: function ExecuteBlockRender({args}: {args: ExecuteBlockArgs}) {
    if (!args?.steps?.length) return null

    const visibleSteps = args.steps.filter(
      (s, i) => !(i === args.steps.length - 1 && s.content?.includes('<solution>')),
    )

    if (!visibleSteps.length) return null

    return (
      <div className='aui-tool-step-list' role='list'>
        {visibleSteps.map((step: AgentStep, i: number) => (
          <AgentToolStepCard
            key={`${step.stepIndex}-${step.node}-${i}`}
            step={step}
            displayStepLabel={formatDisplayStepLabel(visibleSteps, i)}
          />
        ))}
      </div>
    )
  },
})

function asArgRecord(args: unknown): Record<string, unknown> {
  return args && typeof args === 'object' && !Array.isArray(args) ? (args as Record<string, unknown>) : {}
}

function pickArgString(args: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = args[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

/** Plain one-line row for skill playbook loads (no collapsible body). */
export const UseSkillLine: React.FC<{
  skillName: string
  pending?: boolean
  failed?: boolean
}> = memo(({skillName, pending = false, failed = false}) => (
  <div
    className={`ai-chat-tool-row omics-aui-tool omics-aui-tool-use-skill ${pending ? 'is-pending' : failed ? 'is-error' : 'is-success'}`}
    data-testid='omics-use-skill-line'
  >
    <div className='section-summary ai-chat-tool-summary omics-use-skill-summary'>
      <i className='fas fa-book-open' aria-hidden />
      <span className='ai-chat-tool-summary-text'>
        <span className='ai-chat-tool-summary-title'>
          {formatSkillActivityLabel(skillName, {pending, failed})}
        </span>
      </span>
    </div>
  </div>
))

UseSkillLine.displayName = 'UseSkillLine'

type ToolStatus = ToolCallMessagePartProps<Record<string, unknown>, unknown>['status']

/** Explicit props: `makeAssistantToolUI` render loses contextual typing vs bare `FC` in TS 4.9. */
type SimpleStringResultToolProps = ToolCallMessagePartProps<Record<string, unknown>, string>

/** Shared collapsible shell (Bootstrap classes aligned with `OmicsAssistantToolFallback`). */
const KeyedToolCollapsible: React.FC<{
  toolName: string
  summaryLine?: string
  argsText?: string
  resultText?: string
  status: ToolStatus
  isError?: boolean
}> = memo(({toolName, summaryLine, argsText, resultText = '', status, isError}) => {
  const [open, setOpen] = useState(false)
  const displayArgsText = formatToolArgsTextForDisplay(toolName, argsText)
  const label = formatToolDisplayLabel(toolName)
  const pending = status.type === 'running'
  const failed = Boolean(isError) || status.type === 'incomplete'
  const errText = formatIncompleteErrorText(status)
  const resultTrimmed = resultText.trim()

  return (
    <OmicsToolCollapsibleRow
      className='omics-aui-tool omics-aui-tool-keyed'
      title={label}
      subtitle={summaryLine}
      meta={formatToolResultMeta({pending, failed, errorText: errText, resultText: resultTrimmed})}
      pending={pending}
      failed={failed}
      open={open}
      onToggle={() => setOpen((o) => !o)}
    >
      {displayArgsText ? (
        <>
          <div className='ai-chat-tool-section-label'>Parameters</div>
          <OmicsMarkdownBody markdown={`\`\`\`json\n${displayArgsText}\n\`\`\``} />
        </>
      ) : null}
      {!pending && resultTrimmed ? (
        <>
          <div className='ai-chat-tool-section-label'>Result</div>
          <OmicsFormattedTextBlock text={resultTrimmed} className='ai-chat-tool-result-output' />
        </>
      ) : null}
      {failed && errText ? (
        <div role='alert' className='ai-chat-tool-error mt-2'>
          {errText}
        </div>
      ) : null}
    </OmicsToolCollapsibleRow>
  )
})

KeyedToolCollapsible.displayName = 'KeyedToolCollapsible'

export const QueryOmicsboxKbToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'query_omicsbox_kb',
  render: function QueryKbRender({args, result, status, isError, argsText}: SimpleStringResultToolProps) {
    const a = asArgRecord(args)
    const q = pickArgString(a, ['query', 'q', 'question', 'topic'])
    return (
      <KeyedToolCollapsible
        toolName='query_omicsbox_kb'
        summaryLine={q ? `"${q.length > 80 ? `${q.slice(0, 80)}…` : q}"` : undefined}
        argsText={argsText}
        resultText={formatToolResultPlainText(result)}
        status={status}
        isError={isError}
      />
    )
  },
})

export const UseSkillToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'use_skill',
  render: function UseSkillRender({args, status, isError}: SimpleStringResultToolProps) {
    const a = asArgRecord(args)
    const skillName = pickArgString(a, ['skill_name', 'skillName']) || 'skill'
    const pending = status.type === 'running'
    const failed = Boolean(isError) || status.type === 'incomplete'
    return <UseSkillLine skillName={skillName} pending={pending} failed={failed} />
  },
})

export const ReadFileToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'read_file',
  render: function ReadFileRender({args, result, status, isError, argsText}: SimpleStringResultToolProps) {
    const a = asArgRecord(args)
    const legacySkillName = skillNameFromReadFileArgs(a)
    if (legacySkillName) {
      const pending = status.type === 'running'
      const failed = Boolean(isError) || status.type === 'incomplete'
      return <UseSkillLine skillName={legacySkillName} pending={pending} failed={failed} />
    }
    const path = pickArgString(a, ['path', 'target_file', 'file_path', 'filepath'])
    return (
      <KeyedToolCollapsible
        toolName='read_file'
        summaryLine={path || undefined}
        argsText={argsText}
        resultText={formatToolResultPlainText(result)}
        status={status}
        isError={isError}
      />
    )
  },
})

export const SearchInternetToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'search_internet',
  render: function SearchInternetRender({args, result, status, isError, argsText}: SimpleStringResultToolProps) {
    const a = asArgRecord(args)
    const q = pickArgString(a, ['query', 'q', 'search_query'])
    return (
      <KeyedToolCollapsible
        toolName='search_internet'
        summaryLine={q ? `"${q.length > 80 ? `${q.slice(0, 80)}…` : q}"` : undefined}
        argsText={argsText}
        resultText={formatToolResultPlainText(result)}
        status={status}
        isError={isError}
      />
    )
  },
})

export const FetchWebPageToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'fetch_web_page',
  render: function FetchWebPageRender({args, result, status, isError, argsText}: SimpleStringResultToolProps) {
    const a = asArgRecord(args)
    const url = pickArgString(a, ['url'])
    const summary = url
      ? url.length > 80
        ? `${url.slice(0, 80)}…`
        : url
      : undefined
    return (
      <KeyedToolCollapsible
        toolName='fetch_web_page'
        summaryLine={summary}
        argsText={argsText}
        resultText={formatToolResultPlainText(result)}
        status={status}
        isError={isError}
      />
    )
  },
})

const CodeExecToolRender: ToolCallMessagePartComponent<Record<string, unknown>, string> = (
  props: SimpleStringResultToolProps,
) => {
  const {args, result, status, isError, toolName} = props
  const a = asArgRecord(args)
  const {code, other, fenceLanguage, summarySuffix} = extractCodeExecForDisplay(toolName, a)
  return (
    <CodeExecToolCollapsible
      toolName={toolName}
      code={code}
      fenceLanguage={fenceLanguage}
      other={other}
      resultText={formatToolResultPlainText(result)}
      status={status}
      isError={isError}
      summarySuffix={summarySuffix}
    />
  )
}

export const ExecuteToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'execute',
  render: CodeExecToolRender,
})

export const BioExecToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'bio_exec',
  render: CodeExecToolRender,
})

export const PythonExecToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'python_exec',
  render: CodeExecToolRender,
})

export const PythonToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'python',
  render: CodeExecToolRender,
})

function cwlKeyedRender(
  toolName: string,
  formatResult: (result: unknown) => string,
): ToolCallMessagePartComponent<Record<string, unknown>, string> {
  return function CwlKeyedRender({args, result, status, isError, argsText}: SimpleStringResultToolProps) {
    const a = asArgRecord(args)
    const summary = cwlWorkflowSummaryFromTool(toolName, a, result)
    return (
      <KeyedToolCollapsible
        toolName={toolName}
        summaryLine={summary}
        argsText={argsText}
        resultText={formatResult(result)}
        status={status}
        isError={isError}
      />
    )
  }
}

export const ListCwlWorkflowsToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'list_cwl_workflows',
  render: cwlKeyedRender('list_cwl_workflows', formatListCwlWorkflowsResult),
})

export const GetCwlWorkflowParamsToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'get_cwl_workflow_params',
  render: cwlKeyedRender('get_cwl_workflow_params', formatGetCwlWorkflowParamsResult),
})

export const RunCwlWorkflowToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'run_cwl_workflow',
  render: cwlKeyedRender('run_cwl_workflow', formatToolResultPlainText),
})

/** File tools (write/edit/move/copy/make_folder/delete/confirm): label + the cloud: path(s) they act on. */
export function fileOpKeyedRender(
  toolName: string,
): ToolCallMessagePartComponent<Record<string, unknown>, string> {
  return function FileOpKeyedRender({args, result, status, isError, argsText}: SimpleStringResultToolProps) {
    return (
      <KeyedToolCollapsible
        toolName={toolName}
        summaryLine={fileToolSummaryLine(toolName, asArgRecord(args))}
        argsText={argsText}
        resultText={formatToolResultPlainText(result)}
        status={status}
        isError={isError}
      />
    )
  }
}

export const WriteFileToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'write_file',
  render: fileOpKeyedRender('write_file'),
})

export const EditFileToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'edit_file',
  render: fileOpKeyedRender('edit_file'),
})

export const MoveFileToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'move_file',
  render: fileOpKeyedRender('move_file'),
})

export const CopyFileToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'copy_file',
  render: fileOpKeyedRender('copy_file'),
})

export const MakeFolderToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'make_folder',
  render: fileOpKeyedRender('make_folder'),
})

export const DeleteToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'delete',
  render: fileOpKeyedRender('delete'),
})

export const ConfirmFileChangesToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'confirm_file_changes',
  render: fileOpKeyedRender('confirm_file_changes'),
})

/** Agent transfers into the user's cloud storage (start_transfer / get_transfer_status). */
export const StartTransferToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'start_transfer',
  render: (props: SimpleStringResultToolProps) => <TransferToolCard {...props} />,
})

export const GetTransferStatusToolUI = makeAssistantToolUI<Record<string, unknown>, string>({
  toolName: 'get_transfer_status',
  render: (props: SimpleStringResultToolProps) => <TransferToolCard {...props} />,
})

/** Mount under `AssistantRuntimeProvider` next to the thread (generative tool UIs for AG-UI v2). */
export function OmicsAssistantSidebarToolUis(): React.ReactElement {
  return (
    <>
      <ExecuteBlockToolUI />
      <QueryOmicsboxKbToolUI />
      <UseSkillToolUI />
      <ReadFileToolUI />
      <SearchInternetToolUI />
      <FetchWebPageToolUI />
      <ExecuteToolUI />
      <BioExecToolUI />
      <PythonExecToolUI />
      <PythonToolUI />
      <ListCwlWorkflowsToolUI />
      <GetCwlWorkflowParamsToolUI />
      <RunCwlWorkflowToolUI />
      <WriteFileToolUI />
      <EditFileToolUI />
      <MoveFileToolUI />
      <CopyFileToolUI />
      <MakeFolderToolUI />
      <DeleteToolUI />
      <ConfirmFileChangesToolUI />
      <StartTransferToolUI />
      <GetTransferStatusToolUI />
    </>
  )
}
